import { useEffect, useMemo } from "react";
import { SubmitHandler, useForm, useFormState } from "react-hook-form";
import { addYears, differenceInYears, format } from "date-fns";

import { SelectOptionTypes } from "../../../../components/Input";
import { notify } from "../../../../components/Notification";
import {
  FormPanel,
  discardCopyOf,
  formPanelStatusOf,
} from "../../../../components/FormPanel";
import {
  ConfirmDetail,
  formatOptionalCurrency,
  joinDetailParts,
} from "../../../../components/PopUp/confirmShared";
import { useResponsive } from "../../../../hooks/useResponsive";
import { BREAKPOINTS } from "../../../../utils/constants";
import {
  useGetLiabilityTypesQuery,
  useUpdateLiabilityMutation,
} from "../../../../services";
import { LiabilityUpdateRequest } from "../../types";
import { EditLiabilityFields } from "./EditLiabilityFields";
import { FORM_ID } from "./constants";
import { EditLiabilityObject, UpdateLiabilityProps } from "./types";

const PANEL_EYEBROW = "Edit loan details";
const MOBILE_TITLE = "Edit liability";
const ACTION_LABEL = "Save";

export const UpdateLiability = ({
  id,
  liability,
  isOpen,
  onClose,
}: UpdateLiabilityProps) => {
  const {
    data: liabilityTypeData,
    isLoading,
    isError,
    refetch,
  } = useGetLiabilityTypesQuery();
  const [updateLiabilityTrigger] = useUpdateLiabilityMutation();
  const liabilityTypes = useMemo<SelectOptionTypes[]>(
    () =>
      (liabilityTypeData ?? []).map((type) => ({
        label: type.name,
        value: String(type.id),
      })),
    [liabilityTypeData],
  );
  const isCompact = useResponsive(BREAKPOINTS.tabletLarge);

  const defaultValues = useMemo<EditLiabilityObject>(() => {
    const startDate = liability.startDate
      ? new Date(liability.startDate)
      : null;
    const endDate = liability.endDate ? new Date(liability.endDate) : null;
    const term =
      startDate && endDate ? String(differenceInYears(endDate, startDate)) : "";

    return {
      name: liability.name ?? "",
      institution: liability.institution ?? "",
      liabilityType: liability.liabilityType ?? null,
      amount: liability.amount ?? "",
      startDate,
      term,
      endDate,
      paymentFrequency: liability.paymentFrequency ?? null,
      paymentDueDay: liability.paymentDueDay ?? null,
    };
  }, [liability]);

  const form = useForm<EditLiabilityObject>({
    defaultValues,
    mode: "onBlur",
    reValidateMode: "onChange",
  });
  const { control, reset, getValues, setValue, watch } = form;

  const { isDirty, dirtyFields, isSubmitting } = useFormState({ control });

  // Term and end date drive each other; the equality guards stop the two rules looping.
  useEffect(() => {
    const subscription = watch((values, { name }) => {
      if (name === "startDate" || name === "term") {
        const start = values.startDate;
        const years = Number(values.term);
        if (start && years > 0) {
          const nextEnd = addYears(start, years);
          const current = values.endDate;
          if (nextEnd.getTime() !== current?.getTime()) {
            setValue("endDate", nextEnd, { shouldDirty: true });
          }
        }
      } else if (name === "endDate") {
        const start = values.startDate;
        const end = values.endDate;
        if (start && end) {
          const years = differenceInYears(end, start);
          if (years > 0 && String(years) !== values.term) {
            setValue("term", String(years), { shouldDirty: true });
          }
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, setValue]);

  const onSubmit: SubmitHandler<EditLiabilityObject> = async (data) => {
    const patch: LiabilityUpdateRequest = {};

    if (dirtyFields.name) {
      patch.name = data.name;
    }
    if (dirtyFields.institution) {
      patch.institution = data.institution;
    }
    if (dirtyFields.amount) {
      patch.amount = Number.parseFloat(data.amount);
    }
    if (dirtyFields.liabilityType && data.liabilityType) {
      patch.liabilityTypeId = Number(data.liabilityType.value);
    }
    if (dirtyFields.startDate && data.startDate) {
      patch.startDate = format(data.startDate, "yyyy-MM-dd");
    }
    if ((dirtyFields.endDate || dirtyFields.term) && data.endDate) {
      patch.endDate = format(data.endDate, "yyyy-MM-dd");
    }
    if (dirtyFields.paymentFrequency) {
      patch.paymentFrequency = data.paymentFrequency
        ?.value as LiabilityUpdateRequest["paymentFrequency"];
    }
    if (dirtyFields.paymentDueDay) {
      patch.paymentDueDay = Number(data.paymentDueDay);
    }

    try {
      await updateLiabilityTrigger({ id, payload: patch }).unwrap();
      notify.success("Liability updated");
      onClose();
      reset(defaultValues);
    } catch {
      // Keep the panel open on failure; the global middleware shows the toast.
    }
  };

  const buildDiscardDetails = (): ConfirmDetail => {
    const edited = getValues();
    return {
      pill: { label: "LIABILITY", variant: "DEFAULT" },
      title: edited.name || liability.name,
      subtitle: joinDetailParts(
        edited.liabilityType?.label,
        edited.institution,
      ),
      amount: formatOptionalCurrency(edited.amount),
      amountVariant: "neutral",
    };
  };

  const discard = {
    ...discardCopyOf(true, "liability"),
    details: buildDiscardDetails,
  };

  const load = {
    status: formPanelStatusOf({ isLoading, isError }),
    onRetry: refetch,
    loadingLabel: "Loading liability types…",
    errorMessage: "Failed to load liability types. Please try again.",
  };

  const fields = (
    <EditLiabilityFields
      control={control}
      isSubmitting={isSubmitting}
      isCompact={isCompact}
      liabilityTypes={liabilityTypes}
    />
  );

  const isUnchanged = !isDirty;

  return (
    <FormPanel
      open={isOpen}
      onClose={onClose}
      ariaLabel="Edit liability"
      eyebrow={PANEL_EYEBROW}
      title={liability.name}
      form={form}
      resetValues={defaultValues}
      formId={FORM_ID}
      onSubmit={onSubmit}
      submitLabel={ACTION_LABEL}
      isSubmitDisabled={isUnchanged}
      discard={discard}
      mobile={{ title: MOBILE_TITLE, submitLabel: ACTION_LABEL, fields }}
      load={load}
    >
      {fields}
    </FormPanel>
  );
};
